import { useEffect } from 'react';

/**
 * 조회 본문 HTML 후처리 — 링크 새탭 강제, input/form 류 disabled·readonly (레거시 동작).
 * sanitize 는 본 hook 책임 아님(서버 완료 가정). htmlSignature 변경 시에만 재실행해 중복 후처리 방지.
 */
const useContentHtmlPostprocess = (
  ref: React.RefObject<HTMLElement | null>,
  htmlSignature: string | null,
  /** 본문 img 클릭 트리거 — zoom Provider 하위일 때만 non-null. null 이면 cursor/click 미부착. */
  onImageClick?: ((src: string) => void) | null,
): void => {
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    if (!htmlSignature) return;

    // 1) 모든 <a> 태그: target=_blank + rel
    root.querySelectorAll('a').forEach((a) => {
      a.setAttribute('target', '_blank');
      // 기존 rel 이 있으면 보존하면서 noopener/noreferrer 추가
      const existingRel = a.getAttribute('rel') ?? '';
      const tokens = new Set(existingRel.split(/\s+/).filter(Boolean));
      tokens.add('noopener');
      tokens.add('noreferrer');
      a.setAttribute('rel', Array.from(tokens).join(' '));
    });

    // 2) checkbox/radio: disabled (체크 상태는 유지)
    root
      .querySelectorAll<HTMLInputElement>(
        'input[type="checkbox"], input[type="radio"]',
      )
      .forEach((input) => {
        input.disabled = true;
      });

    // 3) 그 외 input: readonly
    root.querySelectorAll<HTMLInputElement>('input').forEach((input) => {
      const type = (input.getAttribute('type') ?? 'text').toLowerCase();
      if (type === 'checkbox' || type === 'radio') return;
      // submit/button 은 4) 에서 disabled 처리
      if (type === 'submit' || type === 'button' || type === 'reset') return;
      input.readOnly = true;
    });

    // 4) form 안의 submit/button/reset 은 disabled
    root
      .querySelectorAll<HTMLButtonElement | HTMLInputElement>(
        'form button, form input[type="submit"], form input[type="button"], form input[type="reset"], button[type="submit"]',
      )
      .forEach((el) => {
        // HTMLButtonElement / HTMLInputElement 모두 disabled 프로퍼티 존재
        (el as HTMLButtonElement).disabled = true;
      });

    // textarea 도 조회에서는 readonly
    root.querySelectorAll('textarea').forEach((el) => {
      (el as HTMLTextAreaElement).readOnly = true;
    });

    // select 는 disabled
    root.querySelectorAll('select').forEach((el) => {
      (el as HTMLSelectElement).disabled = true;
    });

    // 5) 본문 <img> '크게보기' — wrap + 우하단 확대 아이콘 + root 클릭 위임.
    //    img 개별 바인딩은 재진입 후 클릭 불가라 root 위임 1개만 사용. cleanup 은 리스너만 해제 —
    //    wrap/아이콘 원복은 StrictMode 재실행 시 깜빡임이 생겨 하지 않는다.
    const imgCleanups: Array<() => void> = [];
    if (onImageClick) {
      const trigger = onImageClick;
      const EXPAND_ICON_SVG =
        '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 20 20" fill="none">' +
        '<path d="M3.11696 3.10231L6.45499 6.44034L8.124 8.10936M17.1145 17.0999L13.131 13.1164L11.462 11.4474M8.52896 2.18966L2.2042 2.18966L2.2042 8.51442M11.371 17.8108L17.6957 17.8108L17.6957 11.4861" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>';

      // 클릭 이벤트 위임
      const onRootClick = (e: Event) => {
        const target = e.target as HTMLElement | null;
        if (!target) return;
        const img =
          target.tagName === 'IMG'
            ? (target as HTMLImageElement)
            : target.closest('.img-show-detail')?.querySelector('img') ?? null;
        if (img && img.src) trigger(img.src);
      };
      root.addEventListener('click', onRootClick);
      imgCleanups.push(() => root.removeEventListener('click', onRootClick));

      // 각 이미지: 아직 wrap 안 됐으면 wrap + 확대 아이콘 (idempotent — 재실행 시 skip).
      root.querySelectorAll<HTMLImageElement>('img').forEach((img) => {
        img.style.cursor = 'pointer';
        const parent = img.parentElement;
        if (!parent || parent.classList.contains('img-show-detail')) return;

        const wrapper = document.createElement('span');
        wrapper.className = 'img-show-detail';
        wrapper.style.cssText =
          'position:relative;display:inline-block;line-height:0;max-width:100%;';
        parent.insertBefore(wrapper, img);
        wrapper.appendChild(img);

        const icon = document.createElement('i');
        icon.className = 'expand-icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.innerHTML = EXPAND_ICON_SVG;
        icon.style.cssText =
          'position:absolute;right:12px;bottom:12px;display:flex;align-items:center;' +
          'justify-content:center;padding:8px;border-radius:4px;' +
          'background:rgba(240,240,240,0.8);color:#555;cursor:pointer;' +
          'pointer-events:none;transition:background .15s ease,color .15s ease;';
        wrapper.appendChild(icon);

        // hover 시 진하게 — wrapper 가 노드와 함께 사라져 별도 cleanup 불필요.
        wrapper.addEventListener('mouseenter', () => {
          icon.style.background = 'rgba(0, 0, 0, 0.65)';
          icon.style.color = '#fff';
        });
        wrapper.addEventListener('mouseleave', () => {
          icon.style.background = 'rgba(240, 240, 240, 0.8)';
          icon.style.color = '#555';
        });
      });
    }

    return () => {
      imgCleanups.forEach((fn) => fn());
    };
  }, [ref, htmlSignature, onImageClick]);
};

export default useContentHtmlPostprocess;
